import { Box } from '@mui/material';
import StatTile, { StatTone } from '../dashboard/charts/StatTile';
import { SR_ONLY } from './heroStyles';

export interface TrendDelta {
    /** The newer figure, e.g. today's output. */
    current: number;
    /** The figure it is compared with, e.g. yesterday's output. */
    previous: number;
    /** What the comparison is against, shown after the change: "vs yesterday". */
    against: string;
    /** Which direction is good news. Output going up is good; defects going up is not. */
    goodWhen?: 'up' | 'down';
}

export interface TrendChipProps {
    label: string;
    value: string | number;
    unit?: string;
    sub?: string;
    tone?: StatTone;
    /** Optional change line. Shown only when real previous data exists; never invented. */
    delta?: TrendDelta;
    /** A literal help key written where the chip is used. */
    'data-help'?: string;
}

const fmt = (n: number) => n.toLocaleString('en-US');

/**
 * KPI chip: the value block is `StatTile` (reused, not copied) and under it a change pill against a
 * previous figure. The pill never relies on colour alone: it shows an arrow, a sign and words, and
 * a screen reader hears the same thing in plain text.
 */
export default function TrendChip({ label, value, unit, sub, tone, delta, ...rest }: TrendChipProps) {
    let pill: JSX.Element | null = null;
    if (delta) {
        const diff = delta.current - delta.previous;
        const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
        const good = delta.goodWhen ?? 'up';
        const color = dir === 'flat' ? 'text.secondary' : dir === good ? 'success.main' : 'error.main';
        const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '=';
        const sign = diff > 0 ? '+' : diff < 0 ? '−' : '';
        const text = dir === 'flat' ? `no change ${delta.against}` : `${sign}${fmt(Math.abs(diff))} ${delta.against}`;
        const spoken = dir === 'flat'
            ? `No change ${delta.against}: ${fmt(delta.current)}.`
            : `${dir === 'up' ? 'Up' : 'Down'} ${fmt(Math.abs(diff))} ${delta.against}: ${fmt(delta.current)} against ${fmt(delta.previous)}.`;
        pill = (
            <Box data-testid="trend-chip-delta" data-direction={dir} sx={{ px: 1.5, pb: 1.25, mt: -0.75 }}>
                <Box
                    component="span"
                    aria-hidden
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.875, py: 0.125,
                        borderRadius: 999, fontSize: 12, fontWeight: 700, color, bgcolor: 'action.hover',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    <span>{arrow}</span>
                    <span>{text}</span>
                </Box>
                <Box component="span" sx={SR_ONLY}>{spoken}</Box>
            </Box>
        );
    }
    return (
        <Box
            {...rest}
            sx={{
                flex: '1 1 140px', maxWidth: { xs: '100%', sm: 260 }, minWidth: 0,
                display: 'flex', flexDirection: 'column',
                border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper',
            }}
        >
            <StatTile label={label} value={value} unit={unit} sub={sub} tone={tone} />
            {pill}
        </Box>
    );
}
